import { useEffect, useState } from "react";
import { BackIcon } from "@/icons/BackIcon";
import {
  BookFile,
  BookModel,
  PageItemModel,
  PageModel,
  SbPageItemType,
  SbUnitModel,
} from "@/types";
import "./index.scss";
import {
  AwardIcon,
  GoArrow,
  OpenBookIcon,
  PageModeIcon,
  SoundIcon,
  VideoIcon,
} from "@/icons";
import { motion } from "framer-motion";
import BookContents from "./BookContents";
import { useSidebar } from "@/contexts/SideBarContext";
import { useModal } from "@/contexts/ModalContext";
import BookSounds from "./BookSound";
import BookVideos from "./BookVideos";
import { openWebSide } from "@/utility/utils";
import MediaPlayer from "@/components/media-player/mediaplayer";
import BookGames from "./BookGames";
import { SmartBookService } from "@/services/smartBookService";
import { useDispatch, useSelector } from "react-redux";
import { setBook } from "@/redux-store/slices/bookStore";
import { RootState } from "@/redux-store";
import { useTranslation } from 'react-i18next';
import '@/components/Button/index.scss'
import { useNavigate, useParams } from "react-router-dom";

const BookDetail = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'book_detail' });

  const { book } = useSelector((state: RootState) => state.bookReducer);

  const [bookSounds, setBookSounds] = useState<any[]>([]);
  const [bookVideos, setBookVideos] = useState<any[]>([]);
  const [bookGames, setBookGames] = useState<any[]>([]);
  const [bookFiles, setBookFiles] = useState<BookFile[]>([]);
  const [bookDetail, setBookDetail] = useState<BookModel>();
  const { openModal } = useModal();
  const { openSidebar, closeSidebar } = useSidebar();
  const { bookId } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    if (bookId) {
      getBookDetail(Number(bookId));
    }
  }, [bookId]);

  const getBookDetail = async (bookId: number) => {
    const bookDetail = await SmartBookService.getBookById(bookId);
    dispatch(setBook(bookDetail));
    const { sounds, videos, games } = extractPageItems(bookDetail.pages);
    setBookSounds(mapItemsToUnits(bookDetail.units, sounds));
    setBookVideos(mapItemsToUnits(bookDetail.units, videos));
    setBookGames(mapItemsToUnits(bookDetail.units, games));
    setBookFiles(bookDetail.bookFiles);
    setBookDetail(bookDetail);
  };

  const extractPageItems = (pages: PageModel[]) => {
    const sounds: PageItemModel[] = [];
    const videos: PageItemModel[] = [];
    const games: PageItemModel[] = [];

    pages.forEach((page) => {
      sounds.push(
        ...page.pageItems.filter(
          (item) => item.pageItemType === SbPageItemType.Sound
        )
      );
      videos.push(
        ...page.pageItems.filter(
          (item) => item.pageItemType === SbPageItemType.Video
        )
      );
      games.push(
        ...page.pageItems.filter(
          (item) => item.pageItemType === SbPageItemType.Game
        )
      );
    });
    return { sounds, videos, games };
  };

  const mapItemsToUnits = (units: SbUnitModel[], items: PageItemModel[]) => {
    if (items.length === 0) return [];
    return units.map((unit) => ({
      id: unit.id,
      title: unit.name,
      startPage: unit.startPage,
      endPage: unit.endPage,
      items: items.filter((x) => x.bookContentId === unit.id),
    }));
  };

  const navigateBookPage = (bookId: number, pageNumber: number) => {
    navigate(`/smartBook/${bookId}`);
    console.log(bookId, pageNumber);
  };

  const navigatePage = async (pageNo: number) => {
    closeSidebar();
    navigateBookPage(Number(bookId), pageNo);
  };

  const openBookContents = () => {
    openSidebar(
      "bookContents",
      `${t("contents")}`,
      "right",
      <BookContents
        getBookPage={navigatePage}
        bookContents={bookDetail?.units || []}
      />
    );
  };
  const openBookVideos = () => {
    openModal(
      "book-videos",
      "Videos",
      "withOverlay",
      <BookVideos bookId={Number(bookId)} bookVideos={bookVideos}></BookVideos>
    );
  };

  const openBookGames = () => {
    openModal(
      "book-games",
      "Games",
      "withOverlay",
      <BookGames bookId={Number(bookId)} bookGames={bookGames}></BookGames>
    );
  };
  const openBookSounds = () => {
    openModal(
      "book-sounds",
      "Sounds",
      "withOverlay",
      <BookSounds bookId={Number(bookId)} bookSounds={bookSounds}></BookSounds>
    );
  };

  const handleCloseWithoutAnimation = () => {
     navigate("/books")
  };

  const handleFileClick = (file: BookFile) => {
    const fileLink = file.fileLink;
    const fileExtension = fileLink.split(".").pop()?.toLowerCase();
    const isWebLink =
      fileLink.startsWith("http") &&
      !fileExtension?.match(
        /pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|mp3|mp4|wav|avi|mov/i
      );

    // Handle web links
    if (isWebLink) {
      openWebSide(fileLink);
      return;
    }

    // Handle audio/video files
    if (fileExtension?.match(/mp3|wav|ogg|mp4|webm|avi|mov|mkv/i)) {
      const mediaType = fileExtension?.match(/mp3|wav|ogg/i)
        ? "audio"
        : "video";
      openModal(
        "media-player",
        file.title || "Media Player",
        "withOverlay",
        <MediaPlayer
          url={fileLink}
          type={mediaType}
          title={file.title}
          transcript={undefined}
          footer={undefined}
        />
      );
      return;
    }

    // Handle document files and other file types
    // window.electron.openFile(fileLink);
  };

  return (
    <motion.div
      className="book-detail-container"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.4,
        scale: {
          type: "spring",
          damping: 35,
          stiffness: 200,
        },
      }}
    >
      {book && (
        <div className="book-detail-content">
          <div className="book-cover-section">
            <motion.div
              className="image-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <img
                src={book.CoverImageUrl || book.coverImageUrl}
                alt={book.title}
                className="book-cover-image"
                loading="eager"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = '/images/default-book-cover.png'; // Varsayılan bir kitap kapağı resmi
                }}
              />
            </motion.div>
            <motion.h1
              className="book-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.3,
                duration: 0.4,
                ease: "easeOut",
              }}
            >
              {book.title}
            </motion.h1>
          </div>

          <div className="book-info-section">
            <motion.div
              className="info-grid"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.2,
                duration: 0.5,
                ease: "easeOut",
              }}
            >
              <div className="info-card">
                <span className="info-label">{t("branch")}</span>
                <span className="info-value">{book.lessonNames}</span>
              </div>

              <div className="info-card">
                <span className="info-label">{t("class")}</span>
                <span className="info-value">{book.classNames}</span>
              </div>

              <div className="info-card">
                <span className="info-label">{t("page_count")}</span>
                <span className="info-value">{bookDetail?.pages?.length}</span>
              </div>
            </motion.div>

            <div className="action-buttons">
              <button
                className="btn btn-back"
                onClick={handleCloseWithoutAnimation}
              >
                <BackIcon className={undefined} />
                <span>
                  {book.hasGroup
                    ? t("return_to_book_group")
                    : t("return_to_library")}
                </span>
              </button>

              <button
                className="btn btn-contents"
                onClick={() => openBookContents()}
              >
                <OpenBookIcon className={undefined} color={"#fff"} />
                <span>{t("contents")}</span>
              </button>

              <button
                className="btn btn-primary"
                onClick={() => navigateBookPage(Number(bookId), 1)}
              >
                <span>{t("return_to_book")}</span>
                <GoArrow className={undefined} />
              </button>
            </div>

            {(bookSounds?.length > 0 || bookVideos?.length > 0 || bookGames?.length > 0) && (
              <div className="media-buttons">
                {bookSounds?.length > 0 && (
                  <button
                    className="btn-media btn-sound"
                    onClick={() => openBookSounds()}
                  >
                    <SoundIcon color="#fff" />
                    <span>{t("sounds")}</span>
                  </button>
                )}

                {bookVideos?.length > 0 && (
                  <button
                    className="btn-media btn-video"
                    onClick={() => openBookVideos()}
                  >
                    <VideoIcon className={undefined} color={undefined} />
                    <span>{t("videos")}</span>
                  </button>
                )}
                
                {bookGames?.length > 0 && (
                  <button
                    className="btn-media btn-game"
                    onClick={() => openBookGames()}
                  >
                    <AwardIcon className={undefined} color={"#fff"} />
                    <span>{t("games")}</span>
                  </button>
                )}
              </div>
            )}
            
            {bookFiles?.length > 0 && (
              <div className="book-files">
                {bookFiles.map((file) => (
                  <button
                    key={`file-${file.id}`}
                    className="btn"
                    onClick={() => handleFileClick(file)}
                  >
                    <PageModeIcon className={undefined} />
                    <span>{file.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default BookDetail;
